import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import { Navigate } from 'react-router-dom';
import { Page, BlockStack, Card, DataTable, Text, Badge, Tooltip, DescriptionList, Divider, Button, ButtonGroup, InlineStack, Banner, EmptyState } from '@shopify/polaris';
import {
  SaveIcon
} from '@shopify/polaris-icons';

import PageFooter from '../partials/PageFooter';
import LoadingSpinner from '../partials/LoadingSpinner';
import LoadingPlaceholder from '../partials/LoadingPlaceholder';
import RequiresRefreshBanner from '../partials/RequiresRefreshBanner';

import { getBatchResults, getResultRow } from '../../redux/actions/batchInventoryResults';
import { locationsIndex } from '../../redux/actions/locations';

const BatchUpdateResults = ({ shopUrl, payload, batchInventoryResults, loadBatchResults, loadResultRow, locations, loadLocations, requiresRefresh }) => {
  useEffect(() => {
    loadBatchResults(shopUrl, payload);
    loadLocations(shopUrl, payload);
  }, []);

  useEffect(() => {
    if(batchInventoryResults.status === 'success' && batchInventoryResults.batchResults.length > 0) {
      batchInventoryResults.batchResults.forEach((result) => {
        result.batch_body.items.forEach((item) => {
          if(!item.status && item.quantity !== 0) {
            loadResultRow(shopUrl, payload, result.batch_id, item.action_id);
          }
        })
      })
    }
  }, [batchInventoryResults]);

  const downloadActions = (result) => {
    let rows = [['Change ID', 'Change reason', 'Notes', 'Change requested date', 'Change type', 'Product', 'Product Options', 'SKU', 'Barcode', 'Quantity', 'Result']];

    result.batch_body.items.map (item => {
      rows.push([
        result.batch_id,
        result.batch_body.reason,
        result.comments,
        result.date_time,
        result.action_type,
        item?.details?.product?.title,
        (item?.details?.selectedOptions?.length > 0 ? item.details.selectedOptions.map((option) => option.value).join(' | ') : ''),
        item.sku,
        item.barcode,
        item.quantity,
        item.status
      ])
    });

    let csvContent = "data:text/csv;charset=utf-8," 
        + rows.map(e => e.join(",")).join("\n");

    var encodedUri = encodeURI(csvContent);
    window.open(encodedUri);
  }

  const statusBadge = (item) => {
    if(item.status === 'fetching') {
      return <LoadingSpinner size="small" />
    } 

    if(item.status === 'success') {
      return <Badge tone="success"><Text>Success</Text></Badge>
    }

    if(item.quantity === 0) {
      return <Badge tone="info"><Text>No change</Text></Badge>
    }

    return <Tooltip content={item.error_message} dismissOnMouseOut><Badge tone="critical"><Text>Error</Text></Badge></Tooltip>
  }

  const optionBadge = (option) => {
    return <div key={option.name}>
      <Badge 
        tone="info"
        key={option.name}
      >
        <Text>{option.value}</Text>
      </Badge>
       &nbsp;
    </div>
  }

  const locationName = (locationId) => {
    return locations?.locations?.find((location) => location?.id === locationId)?.name;
  }

  const batchRow = (item, index) => {
    return [
      item?.details?.product?.title,
      (item?.details?.selectedOptions?.length > 0 ? item.details.selectedOptions.map((option) => optionBadge(option)) : ''),
      item.sku,
      item.barcode,
      item.quantity,
      statusBadge(item)
    ]
  }

  if(batchInventoryResults.status !== 'success' || locations.status !== 'success') {
    return(
      <Page
        title="Batch Update Results"
        backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
      >
        { requiresRefresh &&
          <RequiresRefreshBanner />
        }
        <BlockStack gap="200">
          <LoadingPlaceholder />
          <LoadingPlaceholder />
          <LoadingPlaceholder />
        </BlockStack>
      </Page>
    )
  }

  if(batchInventoryResults.status === 'success' && batchInventoryResults.batchResults.length === 0) {
    return(
      <Page
        title="Batch Update Results"
        backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
      >
        { requiresRefresh &&
          <RequiresRefreshBanner />
        }
        <Card>
          <EmptyState
            heading="You have no stored batch changes"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>The results of additions, updates or inventory reductions will appear here after they're performed. Live modification information is not saved. This information is saved for 2 weeks after a batch change is made.</p>
          </EmptyState>
        </Card>
      </Page>
    )
  }


  return(
    <Page
      title="Batch Update Results"
      backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
    >
      { requiresRefresh &&
        <RequiresRefreshBanner />
      }
      <BlockStack gap="200">
        <Banner title="Storage Note" onDismiss={() => {}}>
          <p><strong>Batch update results data is only stored for 2 weeks.</strong> Click the "Download Change Details" button to download batch results.</p>
        </Banner>

        { batchInventoryResults.batchResults.map(result =>
          <Card
            key={result.batch_id}
          >
            <BlockStack gap="200">
              <Text variant="headingLg" as="h5">Request Details</Text>
              <DescriptionList
                items={[
                  {
                    term: 'Change ID',
                    description: result.batch_id,
                  },
                  {
                    term: 'Change reason',
                    description: result.batch_body.reason,
                  },
                  {
                    term: 'Location',
                    description: locationName(result.location),
                  },
                  {
                    term: 'Notes',
                    description: result.comments,
                  },
                  {
                    term: 'Change requested date',
                    description: result.date_time
                  },
                  {
                    term: 'Change type',
                    description: result.action_type
                  }
                ]}
              />

              <Divider />
              <Divider />

              <Text variant="headingLg" as="h5">Change Results</Text>

              <DataTable
                headings={[
                  'Name',
                  'Options',
                  'SKU',
                  'Barcode',
                  'Quantity',
                  'Status'
                ]}
                columnContentTypes={[
                  'text',
                  'text',
                  'text',
                  'text',
                  'numeric',
                  'text'
                ]}
                hasZebraStripingOnData
                rows={result.batch_body.items.map((item, index) => batchRow(item, index))}
              />

              <Divider />

              <InlineStack align="end">
                <ButtonGroup>
                  <Button icon={SaveIcon} variant="primary" onClick={() => downloadActions(result)}>Download Change Details</Button>
                </ButtonGroup>
              </InlineStack>

            </BlockStack>
          </Card>
        )}
      </BlockStack>

      <PageFooter stripeBackground='#124b5f' helpLink="inventory-management/view-batch-changes-3/" helpDescription="batch change results" />
    </Page>
  )
}

const mapStateToProps = state => ({
  requiresRefresh: state.activeToken.requiresRefresh,
  batchInventoryResults: state.batchInventoryResults,
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  loadBatchResults: (shopUrl, payload) => dispatch(getBatchResults(shopUrl, payload)),
  loadResultRow: (shopUrl, payload, batch_id, action_id) => dispatch(getResultRow(shopUrl, payload, batch_id, action_id)),
  loadLocations: (shopUrl, payload) => dispatch(locationsIndex(shopUrl, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BatchUpdateResults);